/* global google */

import React from 'react';
import Car from 'images/car.png';
import { isEnRouteOrder } from 'shared/models/order';
import { mapCustomerMarker, mapCourierMarker } from './styles.mod.scss';
import { getFBAvatar } from 'shared/models/user';

const CUSTOMER_MARKER_OFFSET_X = 22;
const CUSTOMER_MARKER_OFFSET_Y = 66;
const COURIER_MARKER_OFFSET_X = 25;
const COURIER_MARKER_OFFSET_Y = 50;

function distanceAlong(path) {
  const pointOne = path[0] || {};
  const pointTwo = path[1] || {};
  if (!pointOne.lat || !pointOne.lng || !pointTwo.lat || !pointTwo.lng) return;
  return Math.sqrt(
    Math.pow(pointTwo.lng - pointOne.lng, 2) +
      Math.pow(pointTwo.lat - pointOne.lat, 2)
  );
}

function fitMapToMarkers({ customerMarker, courierMarker, map }) {
  if (!map || !customerMarker || !courierMarker) return;
  const projection1 = customerMarker.getProjection();
  const projection2 = courierMarker.getProjection();
  if (!projection1 || !projection2) return;
  const pos1 = projection1.fromDivPixelToLatLng({
    x: customerMarker.container.offsetLeft + CUSTOMER_MARKER_OFFSET_X,
    y: customerMarker.container.offsetTop + CUSTOMER_MARKER_OFFSET_Y
  });
  const pos2 = projection2.fromDivPixelToLatLng({
    x: courierMarker.container.offsetLeft + COURIER_MARKER_OFFSET_X,
    y: courierMarker.container.offsetTop + COURIER_MARKER_OFFSET_Y
  });
  const maxLat = Math.max(pos1.lat(), pos2.lat());
  const minLat = Math.min(pos1.lat(), pos2.lat());
  const maxLng = Math.max(pos1.lng(), pos2.lng());
  const minLng = Math.min(pos1.lng(), pos2.lng());
  const bounds = new google.maps.LatLngBounds(
    { lat: minLat, lng: minLng },
    { lat: maxLat, lng: maxLng }
  );
  const padding = 50;
  map.fitBounds(bounds, padding);
}

function normalizeBearing(angle) {
  if (angle > 180) return angle - 360;
  if (angle < -180) return angle + 360;
  return angle;
}

class OrderStatusMap extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      map: null,
      customerMarker: null,
      courierMarker: null,
      courierRoute: null,
      lastCourierLocation: {}
    };
  }

  componentDidMount() {
    const { order, user } = this.props;
    const address = order.delivery_address_detail;
    const location = order.location || {};
    const coordinates = location.coordinates || [];
    const lat = address.lat || coordinates[1];
    const lng = address.lng || coordinates[0];
    const customerLocation = { lat, lng };
    const map = this.drawMap(customerLocation);
    const customerMarker = this.drawCustomerMarker(customerLocation, user, map);
    this.setState({ map, customerMarker });
  }

  componentDidUpdate(prevProps) {
    const { courierLocation, route } = this.props;
    if (this.locationHasChanged(prevProps.courierLocation, courierLocation)) {
      this.updateCourierLocation(courierLocation);
    }
    if (this.routeHasChanged(prevProps.route, route)) {
      this.updateCourierRoute(route);
    }
  }

  drawMap(center) {
    const map = new google.maps.Map(document.getElementById('order-map'), {
      center: center,
      zoom: 15,
      maxZoom: 19,
      disableDefaultUI: true,
      gestureHandling: 'none',
      clickableIcons: false
    });
    return map;
  }

  drawCustomerMarker(customerLocation, user, map) {
    function CustomerImage(position, user) {
      this.position = new google.maps.LatLng(position);
      this.container = document.createElement('div');
      this.container.classList.add(mapCustomerMarker);
      const fbAvatar = getFBAvatar(user);
      if (fbAvatar) {
        const image = document.createElement('img');
        image.src = fbAvatar;
        this.container.appendChild(image);
      }
    }
    CustomerImage.prototype = new google.maps.OverlayView();
    CustomerImage.prototype.onAdd = function() {
      this.getPanes().markerLayer.appendChild(this.container);
    };
    CustomerImage.prototype.draw = function() {
      const projection = this.getProjection();
      if (!projection) return;
      const { x, y } = projection.fromLatLngToDivPixel(this.position);
      this.container.style.left = `${x - CUSTOMER_MARKER_OFFSET_X}px`;
      this.container.style.top = `${y - CUSTOMER_MARKER_OFFSET_Y}px`;
    };
    CustomerImage.prototype.onRemove = function() {
      if (this.container.parentElement) {
        this.container.parentElement.removeChild(this.container);
      }
    };

    const customerMarker = new CustomerImage(customerLocation, user);
    customerMarker.setMap(map);
    return customerMarker;
  }

  locationHasChanged(prevLocation, nextLocation) {
    if (!prevLocation) prevLocation = {};
    const prevCoords = prevLocation.coordinates || [];
    const prevLng = prevCoords[0];
    const prevLat = prevCoords[1];
    const prevBearing = prevLocation.bearing;
    if (!nextLocation) nextLocation = {};
    const nextCoords = nextLocation.coordinates || [];
    const nextLng = nextCoords[0];
    const nextLat = nextCoords[1];
    const nextBearing = nextLocation.bearing;
    return (
      prevLng !== nextLng || prevLat !== nextLat || prevBearing !== nextBearing
    );
  }

  updateCourierLocation(location) {
    let { map, customerMarker, courierMarker } = this.state;
    if (!map || !isEnRouteOrder(this.props.order)) return;

    const coordinates = location.coordinates || [];
    const lng = coordinates[0];
    const lat = coordinates[1];
    if (!lng || !lat) return;
    const position = { lat, lng };
    const bearing = location.bearing;

    if (courierMarker) {
      this.updateCourierMarker({ position, bearing });
    } else {
      const courierMarker = this.initCourierMarker({
        position,
        bearing,
        map,
        customerMarker
      });
      const lastCourierLocation = { position, bearing, timestamp: Date.now() };
      this.setState({ courierMarker, lastCourierLocation });
    }
  }

  initCourierMarker({ position, bearing, map, customerMarker }) {
    function CourierImage(courierPosition, courierBearing, imageSrc) {
      this.position = new google.maps.LatLng(courierPosition);
      this.bearing = courierBearing;
      this.container = document.createElement('div');
      this.container.classList.add(mapCourierMarker);
      this.image = document.createElement('img');
      this.image.src = imageSrc;
      this.container.appendChild(this.image);
    }
    CourierImage.prototype = new google.maps.OverlayView();
    CourierImage.prototype.onAdd = function() {
      this.getPanes().markerLayer.appendChild(this.container);
    };
    CourierImage.prototype.draw = function() {
      const projection = this.getProjection();
      if (!projection) return;
      const { x, y } = projection.fromLatLngToDivPixel(this.position);
      this.container.style.left = `${x - COURIER_MARKER_OFFSET_X}px`;
      this.container.style.top = `${y - COURIER_MARKER_OFFSET_Y}px`;
      if (this.bearing != null) {
        this.image.style.transform = `rotate(${this.bearing}deg)`;
      }
    };
    CourierImage.prototype.update = function({ position, bearing }) {
      this.position = new google.maps.LatLng(position);
      this.bearing = bearing;
      this.draw();
    };
    CourierImage.prototype.onRemove = function() {
      if (this.container.parentElement) {
        this.container.parentElement.removeChild(this.image);
      }
    };

    const courierMarker = new CourierImage(position, bearing, Car);
    courierMarker.setMap(map);
    fitMapToMarkers({ customerMarker, courierMarker, map });
    return courierMarker;
  }

  updateCourierMarker({ position, bearing }) {
    const { courierMarker, customerMarker, map } = this.state;
    const prev = this.state.lastCourierLocation;
    const next = {
      position,
      bearing,
      timestamp: Date.now()
    };
    this.animateCourierMarker(
      { courierMarker, customerMarker, map, prev, next },
      lastCourierLocation => this.setState({ lastCourierLocation })
    );
    return courierMarker;
  }

  animateCourierMarker(
    { courierMarker, customerMarker, map, prev, next },
    onUpdate
  ) {
    if (this.currentAnimations)
      this.currentAnimations.forEach(id => clearTimeout(id));
    this.currentAnimations = [];

    const maxDuration = 3000; // 3 seconds
    const duration = Math.min(next.timestamp - prev.timestamp, maxDuration);
    const prevPosition = prev.position;
    const nextPosition = next.position;
    const prevBearing = prev.bearing || 0;
    const nextBearing = next.bearing != null ? next.bearing : prevBearing;
    const latDelta = nextPosition.lat - prevPosition.lat;
    const lngDelta = nextPosition.lng - prevPosition.lng;
    const bearingDelta = normalizeBearing(nextBearing - prevBearing);

    if (!latDelta && !lngDelta) return;

    const frames = duration * 0.06; // 60 fps

    for (let i = 1; i <= frames; i++) {
      const fraction = i / frames;
      const position = {
        lat: prevPosition.lat + fraction * latDelta,
        lng: prevPosition.lng + fraction * lngDelta
      };
      const bearing = normalizeBearing(prevBearing + fraction * bearingDelta);
      this.currentAnimations.push(
        setTimeout(
          fitBounds => {
            courierMarker.update({ position, bearing });
            if (fitBounds)
              fitMapToMarkers({ courierMarker, customerMarker, map });
            onUpdate({ position, bearing, timestamp: Date.now() });
          },
          fraction * duration,
          i === 1
        )
      );
    }
  }

  routeHasChanged(prevRoute, nextRoute) {
    const prevCoords = prevRoute.coordinates || [];
    const nextCoords = nextRoute.coordinates || [];
    if (prevCoords.length !== nextCoords.length) return true;
    return nextCoords.reduce((res, next, idx) => {
      const prev = prevCoords[idx] || [];
      res || prev[0] !== next[0] || prev[1] !== next[1];
    }, true);
  }

  updateCourierRoute(route) {
    if (!route.coordinates || route.coordinates.length < 2) return;
    const path = route.coordinates.map(([lng, lat]) => ({ lng, lat }));

    let { courierRoute, map } = this.state;
    if (!map) return;

    if (!courierRoute) {
      courierRoute = new google.maps.Polyline({
        path,
        clickable: false,
        strokeColor: '#888',
        map
      });
      this.setState({ courierRoute });
    } else {
      // courier is close to destination, so we can remove the path
      if (path.length === 2 && distanceAlong(path) < 0.00035) {
        courierRoute.setVisible(false);
        this.setState({ courierRoute: null });
      } else {
        courierRoute.setPath(path);
      }
    }
  }

  render() {
    return <div id="order-map" />;
  }
}

export default OrderStatusMap;
